/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { FormattedDate, useIntl } from 'react-intl'
import { upperFirst } from 'lodash'
import { css, jsx } from '@emotion/react'

import type { EuiDescriptionListProps } from '@elastic/eui'
import { EuiDescriptionList, EuiPanel, EuiSpacer, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { LineItem } from '@modules/billing-api/customTypes'
import { useConfig } from '@modules/cui/ConfigContext'
import { CuiHelpTipIcon } from '@modules/cui/HelpTipIcon'

import { numericDateTime } from '@/config/dates'

import InternalField from './InternalField'

const DetailsSection = ({ lineItem }: { lineItem: LineItem }) => {
  const { formatMessage } = useIntl()
  const isAdminConsole = useConfig('APP_NAME') === 'adminconsole'

  const externalItems = [
    {
      title: formatMessage({
        id: 'line-item.details-flyout.details.id',
        defaultMessage: 'Line Item ID',
      }),
      isVisible: true,
      description: lineItem.id,
    },
    {
      title: formatMessage({
        id: 'line-item.details-flyout.details.channel',
        defaultMessage: 'Channel',
      }),
      isVisible: true,
      description: (lineItem.channel || '').toUpperCase(),
    },
    {
      title: formatMessage({
        id: 'line-item.details-flyout.details.currency',
        defaultMessage: 'Currency',
      }),
      isVisible: true,
      description: lineItem.currency,
    },
    {
      title: formatMessage({
        id: 'line-item.details-flyout.details.reason',
        defaultMessage: 'Reason',
      }),
      isVisible: true,
      description: lineItem.reason || '-',
    },
    {
      title: formatMessage({
        id: 'line-item.details-flyout.details.type',
        defaultMessage: 'Type',
      }),
      isVisible: true,
      description:
        {
          prepaid_consumption: formatMessage({
            id: 'line-item.details-flyout.details.type.prepaid_consumption',
            defaultMessage: 'Order line',
          }),
          credit: formatMessage({
            id: 'line-item.details-flyout.details.type.credit',
            defaultMessage: 'Credit',
          }),
        }[lineItem.type] || lineItem.type,
    },
    {
      title: formatMessage({
        id: 'line-item.details-flyout.details.end-type',
        defaultMessage: 'End type',
      }),
      isVisible: true,
      description: (() => {
        if (!lineItem.end_type) {
          return '-'
        }

        if (lineItem.end_type === 'depletion') {
          return (
            <p>
              {upperFirst(lineItem.end_type)}
              <CuiHelpTipIcon
                color='primary'
                iconType='info'
                textSize='s'
                anchorPosition='upCenter'
              >
                <FormattedMessage
                  id='line-item.details-flyout.details.depletion.disclaimer'
                  defaultMessage='This line item will expire/expired when its credits are depleted. No on-demand usage will be associated with this, and it will not be considered for discounting or invoicing purposes.'
                />
              </CuiHelpTipIcon>
            </p>
          )
        }

        return upperFirst(lineItem.end_type)
      })(),
    },
    {
      title: formatMessage({
        id: 'line-item.details-flyout.details.sales-order-number',
        defaultMessage: 'Sales order number',
      }),
      isVisible: !!lineItem.metadata?.sales_order_number,
      description: lineItem.metadata?.sales_order_number,
    },
  ].filter((item) => item.isVisible)

  const internalItems = [
    {
      title: (
        <InternalField>
          <FormattedMessage
            id='line-item.details-flyout.details.reseller-id'
            defaultMessage='External reseller ID'
          />
        </InternalField>
      ),
      isVisible: !!lineItem.external_reseller_id,
      description: lineItem.external_reseller_id,
    },
    {
      title: (
        <InternalField>
          <FormattedMessage
            id='line-item.details-flyout.details.prepaid-charge-after'
            defaultMessage='Prepaid charge after'
          />
        </InternalField>
      ),
      isVisible: !!lineItem.prepaid_charge_after,
      description: <FormattedDate value={lineItem.prepaid_charge_after} {...numericDateTime} />,
    },
    {
      title: (
        <InternalField>
          <FormattedMessage
            id='line-item.details-flyout.details.customer-id'
            defaultMessage='Customer ID'
          />
        </InternalField>
      ),
      isVisible: !!lineItem.metadata?.customer_id,
      description: lineItem.metadata?.customer_id,
    },
    {
      title: (
        <InternalField>
          <FormattedMessage
            id='line-item.details-flyout.details.customer-name'
            defaultMessage='Customer name'
          />
        </InternalField>
      ),
      isVisible: !!lineItem.metadata?.customer_name,
      description: lineItem.metadata?.customer_name,
    },
    {
      title: (
        <InternalField>
          <FormattedMessage
            id='line-item.details-flyout.details.end-user-customer-id'
            defaultMessage='End user customer ID'
          />
        </InternalField>
      ),
      isVisible: !!lineItem.metadata?.end_user_customer_id,
      description: lineItem.metadata?.end_user_customer_id,
    },
    {
      title: (
        <InternalField>
          <FormattedMessage
            id='line-item.details-flyout.details.order-currency'
            defaultMessage='Order currency'
          />
        </InternalField>
      ),
      isVisible: !!lineItem.metadata?.order_currency,
      description: lineItem.metadata?.order_currency,
    },
    {
      title: (
        <InternalField>
          <FormattedMessage
            id='line-item.details-flyout.details.order-id'
            defaultMessage='Order ID'
          />
        </InternalField>
      ),
      isVisible: !!lineItem.metadata?.order_id,
      description: lineItem.metadata?.order_id,
    },
  ].filter((item) => item.isVisible)

  const getListItems = () => {
    if (isAdminConsole) {
      return [...externalItems, ...internalItems]
    }

    return externalItems
  }

  return (
    <EuiPanel paddingSize='m' hasBorder={false} hasShadow={false}>
      <EuiText size='s'>
        <h4>
          <FormattedMessage id='line-item.details-flyout.details.title' defaultMessage='Details' />
        </h4>
      </EuiText>
      <EuiSpacer />
      <EuiDescriptionList
        type='column'
        compressed={true}
        columnGutterSize='m'
        listItems={getListItems() as EuiDescriptionListProps['listItems']}
        css={css`
          dd {
            word-break: break-all;
            max-width: 240px;
          }
        `}
      />
    </EuiPanel>
  )
}

export default DetailsSection
